import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { createTeam, updateTeam } from '../../redux/features/teamSlice';
import { fetchTeamShareRules } from '../../redux/api';
import {
  computeTeamSharesPreview,
  formatShareLabel
} from '../../utils/teamShareUtils';
import TeamAvailabilityCalendar from '../forms/TeamAvailabilityCalendar';
import { X, Check, PieChart } from 'lucide-react';

const TeamForm = ({ isEdit, team, onClose, availableWorkers }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    members: [],
    active: true
  });
  const [shareRules, setShareRules] = useState([]);

  useEffect(() => {
    fetchTeamShareRules()
      .then(({ data }) => setShareRules(data || []))
      .catch(() => setShareRules([]));
  }, []);

  useEffect(() => {
    if (isEdit && team) {
      setFormData({
        name: team.name || '',
        description: team.description || '',
        members: team.members ? team.members.map((m) => (typeof m === 'object' ? m._id : m)) : [],
        active: team.active !== undefined ? team.active : true
      });
    }
  }, [isEdit, team]);

  const selectedWorkers = useMemo(
    () => availableWorkers.filter((w) => formData.members.includes(w._id)),
    [availableWorkers, formData.members]
  );

  const previewShares = useMemo(
    () => computeTeamSharesPreview(selectedWorkers, shareRules),
    [selectedWorkers, shareRules]
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleWorkerSelection = (workerId) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members.includes(workerId)
        ? prev.members.filter((id) => id !== workerId)
        : [...prev.members, workerId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Molimo unesite naziv tima');
      return;
    }

    try {
      if (isEdit) {
        await dispatch(updateTeam({ id: team._id, updatedTeamData: formData })).unwrap();
      } else {
        await dispatch(createTeam({ teamData: formData })).unwrap();
      }
      onClose();
    } catch (error) {
      if (!error?.payload && !error?.response) {
        toast.error(error?.message || 'Greška pri čuvanju tima');
      }
    }
  };

  const baseInputClass =
    'w-full border border-gray-600 rounded px-2 py-1.5 text-xs text-white bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-shadow h-8';
  const labelClass = 'block text-xs font-medium text-white mb-1';

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
      <div className="border-b border-gray-700 p-4 flex justify-between items-center">
        <h2 className="text-lg font-medium text-white">
          {isEdit ? 'Izmena tima' : 'Novi tim'}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-4">
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Naziv tima *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={baseInputClass}
              required
            />
          </div>

          <div>
            <label className={labelClass}>Opis</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`${baseInputClass} h-20`}
              rows={3}
            />
          </div>

          <div>
            <label className={labelClass}>Članovi tima</label>
            {availableWorkers && availableWorkers.length > 0 ? (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto bg-gray-900 p-2 rounded border border-gray-800">
                {availableWorkers.map((worker) => {
                  const isSelected = formData.members.includes(worker._id);
                  return (
                    <label
                      key={worker._id}
                      htmlFor={`worker-${worker._id}`}
                      className={`flex items-center gap-3 p-2 rounded-lg border transition-colors cursor-pointer ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-gray-700 hover:border-gray-500 hover:bg-gray-800'
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={`worker-${worker._id}`}
                        checked={isSelected}
                        onChange={() => handleWorkerSelection(worker._id)}
                        className="sr-only"
                      />
                      <span
                        className={`flex items-center justify-center w-5 h-5 rounded-md border transition-colors ${
                          isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-500'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && <Check size={14} className="text-white" />}
                      </span>
                      <span className="flex-1 text-sm text-white">
                        {worker.firstName} {worker.lastName}
                        <span className="block text-xs text-gray-400">
                          {formatShareLabel(worker.specialization)} (k={worker.specializationCoefficient || '—'})
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400 mt-2">Nema dostupnih radnika</p>
            )}
          </div>

          {formData.members.length >= 1 && (
            <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-3">
              <div className="flex items-center gap-2 mb-2">
                <PieChart size={16} className="text-purple-400" />
                <span className="text-sm font-medium text-white">Udele u timu</span>
              </div>
              <div className="space-y-2">
                {previewShares.map((share) => {
                  const worker = selectedWorkers.find((w) => w._id === share.workerId);
                  if (!worker) return null;
                  return (
                    <div
                      key={share.workerId}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-300">
                        {worker.firstName} {worker.lastName}
                        <span className="text-xs text-gray-500 ml-1">
                          ({formatShareLabel(share.specialization)})
                        </span>
                      </span>
                      <span className="font-semibold text-purple-300">
                        {share.sharePercent}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {formData.members.length >= 1 && (
            <div>
              <label className={labelClass}>Kalendar dostupnosti tima (pregled)</label>
              <TeamAvailabilityCalendar
                team={{ ...(team || {}), members: formData.members, active: formData.active }}
                workers={availableWorkers}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-700/60 bg-gray-900/35 px-3 py-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="active"
                id="active"
                checked={formData.active}
                onChange={handleChange}
                className="rounded border-gray-400 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-white whitespace-nowrap">Aktivan tim</span>
            </label>
            <span className="text-[11px] text-gray-500">
              Dostupnost po danima zavisi od kalendara svih članova
            </span>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-700 rounded-md hover:bg-gray-600 transition-colors"
          >
            Otkaži
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            {isEdit ? 'Sačuvaj izmene' : 'Kreiraj tim'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamForm;
